import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ClarityIcon from '@mui/icons-material/TrackChanges';
import ContextIcon from '@mui/icons-material/Description';
import GoalIcon from '@mui/icons-material/EmojiObjects';
import StructureIcon from '@mui/icons-material/Build';
import OverallComment from './OverallComment';

const iconMap = {
    "Clarity & Specificity": <ClarityIcon />,
    "Context & Constraints": <ContextIcon />,
    "Goal Alignment & Intent": <GoalIcon />,
    "Output Structure & Measurability": <StructureIcon />,
};

const getScoreColor = (score) => {
    if (score <= 4) return '#B3261E'; // Error Red
    if (score <= 7) return '#FFB238'; // Warning Amber
    return '#2E7D32'; // Success Green
};

const EXTENSION_PX = 30; // How far the line extends into the center PNG area

const ConnectorSVG = ({ draw }) => {
    const svgRef = useRef(null);

    // Run draw logic on component mount and whenever the draw function changes (i.e., state updates)
    useEffect(() => {
        draw(svgRef.current);
    }, [draw]);

    // Ensure redraw on resize for responsive layout changes
    useEffect(() => {
        const handleResize = () => {
            draw(svgRef.current);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [draw]);

    return (
        <svg
            ref={svgRef}
            id="connectors"
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                overflow: 'visible',
                zIndex: 1,
                pointerEvents: 'none', // CRITICAL FIX: Allow mouse events to pass through SVG
            }}
        />
    );
};

const MindMapScoreCard = React.forwardRef(({ axis, position, isMobileView }, ref) => {
    const icon = iconMap[axis.name];
    const color = getScoreColor(axis.score);
    const borderStyle = '5px solid #522A27'; // Increased border thickness to 5px

    const [isJustificationVisible, setIsJustificationVisible] = useState(false);
    const timeoutRef = useRef(null);

    const theme = useTheme();
    // Y-percentages for connection points (relative to card height)
    const yConnectionPercent = position.includes('top') ? 80 : 20;

    const clearTimer = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    // 1. Universal Hover Handlers (Desktop relies on these)
    const handleMouseEnter = () => {
        clearTimer();
        setIsJustificationVisible(true);
    };

    const handleMouseLeave = () => {
        setIsJustificationVisible(false);
    };

    const handleClick = useCallback(() => {
        clearTimer();

        const newState = !isJustificationVisible;
        setIsJustificationVisible(newState);

        // If toggling ON, set a timeout to hide it after 3 seconds (for mobile tap)
        if (newState) {
            timeoutRef.current = setTimeout(() => {
                setIsJustificationVisible(false);
            }, 3000);
        }
    }, [isJustificationVisible]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => clearTimer();
    }, []);

    const positionStyles = {
        // Default for Mobile (Stacked grid item)
        width: '100%',
        minHeight: '120px', // Increased minimum height for mobile
        p: 0, // Removed card padding, adding it internally

        // Desktop/Diagram Positioning (900px breakpoint)
        '@media (min-width: 900px)': {
            position: 'absolute',
            width: '280px', // Wider card 
            minHeight: '110px', // Taller height for the 3-section layout, can grow
            p: 0,
            zIndex: 15, // CRITICAL FIX: Ensure cards are above the SVG (which is z-index: 1)
            
            // Positioning adjusted for 110px height card
            // Top cards: Centered at 50% - 75px. 50% - 130px top edge.
            // Bottom cards: Centered at 50% + 75px. 50% + 20px top edge.
            top: position.includes('top') ? 'calc(50% - 130px)' : 'calc(50% + 20px)',

            // Horizontal positioning now pushes cards outwards, maintaining a 15px gap from the container's edge.
            // This becomes responsive for viewports between 900px and 960px.
            ...(position.includes('left')
                ? { left: '10px' }
                : { right: '10px' }
            ),
        },
    };

    return (
        <Box
            ref={ref}
            data-y-percent={yConnectionPercent} // Pass data attribute for connector calculation
            data-side={position.includes('left') ? 'left' : 'right'}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            sx={{
                ...positionStyles,
                bgcolor: '#F7F4EF',
                border: borderStyle, // Applied 5px border
                borderRadius: 2,
                boxSizing: 'border-box',
                cursor: 'pointer', // Indicates interactivity
                overflow: 'hidden',
                transition: 'box-shadow 0.15s ease-out, transform 0.15s ease-out',
                '&:hover': isMobileView ? {} : {
                    // Subtle visual feedback on desktop hover
                    boxShadow: theme.shadows[4],
                    // transform: 'translateY(-2px)',
                    zIndex: 20, // Bring hovered card slightly above others
                },
                display: 'flex', // Use flex column for the 3 vertical sections
                flexDirection: 'column',
            }}
        >
            
            {/* SECTION 1: TOP BAR (Name of the Score) */}
            <Box sx={{
                bgcolor: '#522A27', // Use dark border color for the header
                p: 1,
                textAlign: 'center',
                flexShrink: 0,
                height: '30px', // Fixed height for toolbar
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', lineHeight: 1, color: 'white', fontSize: { xs: '0.75rem', md: '0.8rem' } }}
                >
                    {axis.name.toUpperCase()}
                </Typography>
            </Box>

            {/* SECTION 2: TRANSITIONING CONTAINER */}
            <Box sx={{
                position: 'relative',
                flexGrow: 1,
                display: 'flex',
                alignItems: 'stretch',
                minHeight: '80px', // 110px total min height - 30px header
            }}>

                {/* JUSTIFICATION VIEW - In flow, but animates height */}
                <Box sx={{
                    p: { xs: 1.5, md: 2 },
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'white',
                    opacity: isJustificationVisible ? 1 : 0,
                    maxHeight: isJustificationVisible ? '500px' : 0,
                    overflow: 'hidden',
                    transition: 'opacity 0.3s ease-in-out, max-height 0.3s ease-in-out',
                }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 'bold',
                            textAlign: 'center',
                            color: 'text.primary',
                            fontSize: { xs: '0.75rem', md: '0.8rem' }
                        }}
                    >
                        {axis.justification}
                    </Typography>
                </Box>

                {/* MAIN VIEW (Icon + Score) - Absolute, fades opposite to justification */}
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'stretch',
                    opacity: isJustificationVisible ? 0 : 1,
                    pointerEvents: isJustificationVisible ? 'none' : 'auto',
                    transition: 'opacity 0.3s ease-in-out',
                    background: `linear-gradient(to right, ${alpha(theme.palette.secondary.main, 0.3)}, white 30%)`,
                }}>
                    {/* 2A. Left Icon Section */}
                    <Box sx={{
                        flexShrink: 0,
                        width: { xs: '50%', md: '90px' },
                        color: '#522A27', // Use border color for icon
                        fontSize: { xs: 35, md: 40 }, // Base font size for the 2em multiplier
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 0, // Helps with vertical centering
                    }}>
                        {React.cloneElement(icon, { sx: { fontSize: '2em' } })}
                    </Box>

                    {/* 2B. Right Score Section - FORCED HORIZONTAL LAYOUT */}
                    <Box sx={{
                        flexGrow: 1,
                        // Vertically center the inner content container
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 1.5,
                    }}>
                        {/* Horizontal Container for Score and Suffix */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'baseline', // Align score and /10 along their baseline
                            mr: 2, // Space from the right edge
                        }}>
                            <Typography
                                variant="h2"
                                component="div" // Use div to control spacing better
                                sx={{
                                    fontWeight: '900',
                                    color,
                                    lineHeight: 1, // Keep score tight
                                    fontSize: { xs: '3rem', md: '4rem' },
                                    mr: 0.5, // Space between score number and /10
                                }}
                            >
                                {axis.score}
                            </Typography>
                            <Typography
                                component="span"
                                variant="h6" // Make /10 text significantly larger than subtitle1
                                sx={{ color, fontWeight: 'bold', lineHeight: 1, fontSize: { xs: '1rem', md: '1.4rem' } }}>
                                /10
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
});

const positionMapping = ['top-left', 'bottom-left', 'top-right', 'bottom-right']; // Reordered to match the visual flow (L1, L2, R1, R2)

function ScoreDashboard({ axes = [], overallComment, analysisContainerRef }) {
    const containerRef = useRef(null);
    const centerRef = useRef(null);
    // 4 refs for the 4 cards
    const cardRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
    const theme = useTheme();
    // Use the breakpoint defined in the desktop layout logic (min-width: 900px)
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const isMobileView = !isDesktop;
    const mobileAndyWrapperRef = useRef(null);
    const hatRef = useRef(null);

    const [easterEggStep, setEasterEggStep] = useState(0);
    const [hatAnimation, setHatAnimation] = useState({ key: 0, name: 'none' });

    const [isBlinking, setIsBlinking] = useState(false);
    const [isWinking, setIsWinking] = useState(false);
    const animationTimeoutRef = useRef(null);
    const blinkCountRef = useRef(0);
    // Function to get a random number between 5 and 10 (inclusive)
    const getRandomWinkThreshold = () => Math.floor(Math.random() * 6) + 5;
    const winkThresholdRef = useRef(getRandomWinkThreshold());

    const [isHopping, setIsHopping] = useState(false); // For body animation
    const [canClickHop, setCanClickHop] = useState(true);
    const hopTimeoutRef = useRef(null);

    const handleAndyClick = () => {
        if (isMobileView && canClickHop && !isHopping) {
            setCanClickHop(false); // Start cooldown
            setIsHopping(true); // Trigger the body hop

            // Allow another click after 2 seconds
            setTimeout(() => setCanClickHop(true), 2000);
            // Reset body hopping state after animation finishes (1s)
            setTimeout(() => setIsHopping(false), 1000);

            // Determine the next step in the 1-5 sequence
            const nextStep = (easterEggStep % 5) + 1;

            // Apply hat animation based on the step
            if (nextStep <= 3) {
                // Hops 1, 2, 3: Increasing height
                const hopHeight = -60 * nextStep; // -60, -120, -180
                setHatAnimation({
                    key: Date.now(), // Re-trigger animation
                    name: 'hat-hop-easter-egg',
                    duration: '1s',
                    styles: { '--hat-hop-height': `${hopHeight}px` }
                });
            } else if (nextStep === 4) {
                // Hop 4: Launch hat to top, fall to his feet
                if (hatRef.current && mobileAndyWrapperRef.current && analysisContainerRef.current) {
                    const hatRect = hatRef.current.getBoundingClientRect();
                    const andyWrapperRect = mobileAndyWrapperRef.current.getBoundingClientRect();
                    const containerRect = analysisContainerRef.current.getBoundingClientRect();

                    // Distance from hat's start to the container's top border
                    const launchHeight = containerRect.top - hatRect.top;
                    // Distance from hat's start to land at Andy's feet (bottom of his wrapper minus hat height)
                    const fallDistance = andyWrapperRect.height - hatRect.height;
                    
                    setHatAnimation({
                        key: Date.now(),
                        name: 'hat-launch-and-fall',
                        duration: '1.5s', // A slightly longer animation
                        styles: {
                            '--launch-height': `${launchHeight}px`,
                            '--fall-distance': `${fallDistance}px`,
                        }
                    });
                }
            } else if (nextStep === 5) {
                // Hop 5: Pop hat from ground back to head
                setHatAnimation({
                    key: Date.now(),
                    name: 'hat-return',
                    duration: '1s',
                    styles: hatAnimation.styles // Reuse fall-distance from previous step
                });
            }
            // Update the step counter, wrapping back to 0 after step 5
            setEasterEggStep(nextStep % 5);
        }
    };

    // Random hopping effect
    useEffect(() => {
        const scheduleNextHop = () => {
            clearTimeout(hopTimeoutRef.current);
            const delay = Math.random() * 5000 + 5000; // Random delay 5-10 seconds

            hopTimeoutRef.current = setTimeout(() => {
                // Only trigger a random hop if no easter egg is in progress
                if (!isHopping && easterEggStep === 0) {
                    setIsHopping(true);
                    setHatAnimation({ key: Date.now(), name: 'hat-hop', duration: '1s', styles: {} });
                    setTimeout(() => setIsHopping(false), 1000);
                }
                if (isMobileView) scheduleNextHop();
            }, delay);
        };

        if (isMobileView) scheduleNextHop();
        return () => clearTimeout(hopTimeoutRef.current);
    }, [isMobileView, isHopping, easterEggStep]); // Rerun logic if these states change

    // Blinking / Winking effect
    useEffect(() => {
        const scheduleNextAction = () => {
            clearTimeout(animationTimeoutRef.current);
            const delay = Math.random() * 4000 + 1000;

            animationTimeoutRef.current = setTimeout(() => {
                // Decide whether to wink or blink
                if (blinkCountRef.current >= winkThresholdRef.current) {
                    // Time to wink
                    setIsWinking(true);
                    blinkCountRef.current = 0; // Reset counter
                    winkThresholdRef.current = getRandomWinkThreshold(); // Get a new random threshold

                    animationTimeoutRef.current = setTimeout(() => {
                        setIsWinking(false);
                        scheduleNextAction(); // Schedule the next action
                    }, 400); // Wink duration is longer
                } else {
                    // Time to blink
                    setIsBlinking(true);
                    blinkCountRef.current += 1; // Increment blink counter

                    animationTimeoutRef.current = setTimeout(() => {
                        setIsBlinking(false);
                        scheduleNextAction(); // Schedule the next one
                    }, 150); // Blink duration
                }
            }, delay);
        };

        scheduleNextAction(); // Start the animation loop

        return () => { // Cleanup function to run when component unmounts
            clearTimeout(animationTimeoutRef.current);
        };
    }, []); // Empty dependency array ensures this effect runs only once on mount

    const drawConnectors = (svgElement) => {
        if (!svgElement || !containerRef.current || !centerRef.current || isMobileView) {
            svgElement.innerHTML = ''; // Clear SVG if not desktop
            return;
        }

        // Clear previous paths
        svgElement.innerHTML = '';

        const containerRect = containerRef.current.getBoundingClientRect();
        const centerRect = centerRef.current.getBoundingClientRect();

        // Edges of the center box relative to the container/SVG
        const centerLeftEdge = centerRect.left - containerRect.left;
        const centerRightEdge = centerRect.right - containerRect.left;

        const createPath = (d) => {
            const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
            path.setAttribute("d", d);
            path.setAttribute("stroke", "#522A27"); // Line color
            path.setAttribute("stroke-width", "5"); // Increased line thickness to 5px
            path.setAttribute("fill", "none");
            svgElement.appendChild(path);
        };

        cardRefs.forEach((ref) => {
            const box = ref.current;
            if (!box) return;

            const rect = box.getBoundingClientRect();
            // Read custom properties stored in data attributes
            const yPercent = parseFloat(box.dataset.yPercent) / 100;
            const side = box.dataset.side;

            // 1. Calculate the target Y coordinate (relative to container)
            // This is the specific vertical point (20% or 80%) on the side box.
            const targetY = rect.top + (rect.height * yPercent) - containerRect.top;

            let startX_line, endX_line;

            // --- Logic to Extend Line under PNG (EXTENSION_PX) ---
            if (side === 'left') {
                // Line starts 30px *into* the PNG from the centerLeftEdge
                startX_line = centerLeftEdge + EXTENSION_PX;
                // Line ends at the box's right edge
                endX_line = rect.right - containerRect.left;
            } else if (side === 'right') {
                // Line starts 30px *into* the PNG from the centerRightEdge
                startX_line = centerRightEdge - EXTENSION_PX;
                // Line ends at the box's left edge
                endX_line = rect.left - containerRect.left;
            }

            // Path is a single straight horizontal line
            const d = `M${startX_line},${targetY} L${endX_line},${targetY}`;
            createPath(d);
        });
    };

    return (
        <Box ref={containerRef} sx={{
            position: 'relative',
            // Removed pt: { xs: 0, md: 3 } to reduce top whitespace
            pb: { xs: 0, md: 3 },
            gap: 3,
            minHeight: { md: '400px' } // Ensure enough height for the diagram
        }}>

            {/* SVG Layer (only for desktop) */}
            {isDesktop && <ConnectorSVG draw={drawConnectors} />}

            {/* Mobile Grid Layout (for screens < 900px) */}
            <Box sx={{
                display: { xs: 'grid', md: 'none' },
                gridTemplateColumns: '1fr 1fr',
                gap: 3,
                mb: 3,
            }}>
                {axes.map((axis, index) => (
                    <MindMapScoreCard
                        key={axis.name}
                        axis={axis}
                        position={positionMapping[index]}
                        isMobileView={isMobileView}
                    />
                ))}
            </Box>

            {/* Desktop Diagram Container (for screens >= 900px) */}
            <Box sx={{
                display: { xs: 'none', md: 'block' },
                height: '350px', // Fixed height for diagram on desktop
                position: 'relative',
                width: '100%',
                maxWidth: '980px', // Panel max-width increased as requested
                mx: 'auto',
            }}>

                {/* Character Centerpiece */}
                <Box
                    ref={centerRef}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '150px', // Smaller character box
                        height: '200px', // Taller character box
                        zIndex: 10,
                        // This container is the positioning context for the text and image.
                    }}
                >
                    <Typography
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: '50%',
                            transform: 'translate(-50%, -100%)', // Moves text above the container
                            fontFamily: '"VT323", monospace',
                            color: 'secondary.main',
                            fontSize: '2.5rem',
                            lineHeight: 1,
                            letterSpacing: '-2px', // Added letter-spacing for desktop
                        }}
                    >
                        A.N.D.Y.
                    </Typography>
                    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                        <Box
                            component="img"
                            src="/andy-plain.png"
                            alt="Andy the Analyzer"
                            sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                        <Box
                            component="img"
                            src="/andy-blink.png"
                            alt=""
                            sx={{
                                position: 'absolute',
                                top: 0, left: 0,
                                width: '100%', height: '100%',
                                objectFit: 'contain',
                                display: isBlinking ? 'block' : 'none'
                            }}
                        />
                        <Box
                            component="img"
                            src="/andy-wink.png"
                            alt=""
                            sx={{
                                position: 'absolute',
                                top: 0, left: 0,
                                width: '100%', height: '100%',
                                objectFit: 'contain',
                                display: isWinking ? 'block' : 'none'
                            }}
                        />
                        <Box
                            component="img"
                            src="/andy-circle.png"
                            alt="antenna signal"
                            sx={{
                                position: 'absolute',
                                // Positioning based on original asset dimensions
                                width: '12.5%', // 41px / 327px
                                height: 'auto',
                                top: '2.3%', // (31px - 20.5px) / 459px
                                left: '79.1%', // Adjusted slightly left from 79.7%
                                // Animation properties
                                transformOrigin: 'center',
                                animation: 'radiate 1s infinite ease-out',
                                pointerEvents: 'none', // Prevent image from being interactive
                            }}
                        />
                        <Box
                            component="img"
                            src="/andy-hat.png"
                            alt="Andy's hat"
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '77.7%', // Ratio of hat (254px) to body (327px)
                                height: 'auto',
                            }}
                        />
                    </Box>
                </Box>

                {/* Score Cards (Absolute Positioning) */}
                {axes.map((axis, index) => (
                    <MindMapScoreCard
                        key={axis.name}
                        axis={axis}
                        position={positionMapping[index]}
                        ref={cardRefs[index]} // Pass ref
                        isMobileView={isMobileView}
                    />
                ))}
            </Box>

      {/* NEW: Mobile Character + Comment Section */}
      <Box sx={{
          display: { xs: 'flex', md: 'none' },
          alignItems: 'flex-end', // Align items to the bottom
          mt: 4, // Space above this section
          gap: 1.5,
      }}>
          <Box id="mobile-andy-wrapper" ref={mobileAndyWrapperRef} onClick={handleAndyClick} sx={{
              width: '100px',
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'pointer',
              position: 'relative', // Added to create a positioning context for the absolute text
          }}>
              <Typography sx={{
                  position: 'absolute',
                  bottom: '-24px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '100%', // Ensure text wraps if needed
                  textAlign: 'center',
                  fontFamily: '"VT323", monospace',
                  fontSize: '1.4em',
                  color: '#3d3429',
                  zIndex: 2, // Ensure text is on top of the image
              }}>
                  A.N.D.Y.
              </Typography>
              <Box id="mobile-andy-animator" sx={{
                  width: '100%',
                  animation: isHopping ? `hop 1s ease-out 1` : 'none',
              }}>
                <Box sx={{ position: 'relative', width: '100%', height: 'auto' }}>
                    <Box
                        component="img"
                        src="/andy-plain.png"
                        alt="Andy the Analyzer"
                        sx={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                    <Box
                        component="img"
                        src="/andy-blink.png"
                        alt=""
                        sx={{
                            position: 'absolute',
                            top: 0, left: 0,
                            width: '100%',
                            height: 'auto',
                            display: isBlinking ? 'block' : 'none'
                        }}
                    />
                    <Box
                        component="img"
                        src="/andy-wink.png"
                        alt=""
                        sx={{
                            position: 'absolute',
                            top: 0, left: 0,
                            width: '100%',
                            height: 'auto',
                            display: isWinking ? 'block' : 'none'
                        }}
                    />
                  <Box
                      component="img"
                      src="/andy-circle.png"
                      alt="antenna signal"
                      sx={{
                          position: 'absolute',
                          // Positioning based on original asset dimensions
                          width: '12.5%', // 41px / 327px
                          height: 'auto',
                          top: '2.3%', // (31px - 20.5px) / 459px
                          left: '79.1%', // Adjusted slightly left from 79.7%
                          // Animation properties
                          transformOrigin: 'center',
                          animation: 'radiate 1s infinite ease-out',
                          pointerEvents: 'none', // Prevent image from being interactive
                      }}
                  />
                    <Box
                        component="img"
                        ref={hatRef}
                        key={hatAnimation.key}
                        src="/andy-hat.png"
                        alt="Andy's hat"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '77.7%', // Ratio of hat (254px) to body (327px)
                            height: 'auto',
                            animationName: hatAnimation.name,
                            animationDuration: hatAnimation.duration,
                            animationFillMode: 'forwards', // Keeps hat in its final state
                            ...hatAnimation.styles,
                        }}
                    />
                </Box>
              </Box>
          </Box>
          {/* This wrapper ensures the comment bubble grows to fill remaining space without touching the edge */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <OverallComment comment={overallComment} variant="mobile" />
          </Box>
      </Box>

      {/* DESKTOP Overall Comment (Now hidden on mobile) */}
      <Box sx={{
          display: { xs: 'none', md: 'block' },
          mt: { md: 0 },
          maxWidth: { md: '980px' }, // Match diagram container width
          mx: 'auto', // Match diagram container centering
          px: '10px', // Align with panel's 10px offset from the edge
          boxSizing: 'border-box',
        }}>
        {/* This inner box constrains the comment bubble's max width and centers it */}
        <Box sx={{ maxWidth: '800px', mx: 'auto'}}>
          <OverallComment comment={overallComment} variant="desktop" />
        </Box>
      </Box>
    </Box>
  );
}

export default ScoreDashboard;
